import { useEffect, useId, useRef, type ReactNode } from 'react'
import { ArrowLeft, ArrowUpRight, CalendarDays, Clock, List, MapPin, Ticket, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { EventDetail, EventSummary } from '@/types'
import { DAYS_AHEAD } from '@/services/events'
import { clock, dateTile, eventStatus, lotStatus, money, when, TONE_CHIP, TONE_TEXT } from '@/lib/events'
import { cn } from '@/lib/utils'

const ICON_BUTTON = 'grid size-11 shrink-0 place-items-center rounded-full outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring'

/** A bottom sheet on a phone, a panel at the map's left on a wider screen. Escape closes it. */
function Sheet({ labelledBy, onEscape, children }: { labelledBy: string; onEscape: () => void; children: ReactNode }) {
  const escape = useRef(onEscape)
  escape.current = onEscape
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') escape.current() }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [])
  return (
    <section
      id="event-sheet"
      role="dialog"
      aria-labelledby={labelledBy}
      className={cn(
        'fixed inset-x-0 bottom-0 z-[60] flex max-h-[62dvh] flex-col overflow-hidden rounded-t-2xl border border-border bg-card text-card-foreground shadow-2xl',
        'animate-in slide-in-from-bottom-8 fade-in duration-200 motion-reduce:animate-none',
        'md:inset-x-auto md:bottom-auto md:left-4 md:top-[120px] md:max-h-[calc(100dvh-164px)] md:w-[384px] md:rounded-2xl md:slide-in-from-bottom-0 md:slide-in-from-left-4',
      )}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {children}
    </section>
  )
}

/** The event's image, or its category's colors where it has none. */
function EventImage({ event, className }: { event: EventSummary; className?: string }) {
  return event.imageUrl ? (
    <img src={event.imageUrl} alt="" className={cn('object-cover', className)} />
  ) : (
    <div className={cn('grid place-items-center bg-gradient-to-br from-violet-600 to-indigo-900 text-white/80', className)}>
      <Ticket className="size-1/3 max-h-12 max-w-12" aria-hidden />
    </div>
  )
}

function Chip({ className, children }: { className?: string; children: ReactNode }) {
  return <span className={cn('inline-flex h-6 items-center rounded-full px-2.5 text-xs font-semibold', className)}>{children}</span>
}

const CATEGORY: Record<EventSummary['category'], string> = {
  sports: 'Sports', concert: 'Concert', festival: 'Festival', conference: 'Conference', other: 'Event',
}

interface EventCardProps {
  /** The event as the list gave it, drawn at once; its lots come with `detail`. */
  summary: EventSummary | undefined
  detail: EventDetail | undefined
  failed: boolean
  onBack?: () => void
  onClose: () => void
}

/** One event: what, where and when, its lots with price and what is left, and "Book parking". */
export function EventCard({ summary, detail, failed, onBack, onClose }: EventCardProps) {
  const titleId = useId()
  const event = detail ?? summary
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => heading.current?.focus(), [event?.id])

  const status = event && eventStatus(event)
  const bookable = event && event.state !== 'canceled' && event.state !== 'ended'
  return (
    <Sheet labelledBy={titleId} onEscape={onBack ?? onClose}>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="relative">
          {event ? <EventImage event={event} className="h-24 w-full md:h-44" /> : <div className="h-24 w-full animate-pulse bg-muted md:h-44" />}
          <div className="absolute inset-x-0 top-0 flex justify-between p-2">
            {onBack ? (
              <button type="button" onClick={onBack} aria-label="Back to events" className={cn(ICON_BUTTON, 'bg-black/45 text-white backdrop-blur hover:bg-black/60')}>
                <ArrowLeft size={20} />
              </button>
            ) : <span />}
            <button type="button" onClick={onClose} aria-label="Close" className={cn(ICON_BUTTON, 'bg-black/45 text-white backdrop-blur hover:bg-black/60')}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="space-y-3 p-4 md:space-y-4">
          {event && status ? (
            <>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  <Chip className="bg-violet-500/10 text-violet-700 dark:text-violet-300">{CATEGORY[event.category]}</Chip>
                  <Chip className={TONE_CHIP[status.tone]}>{status.label}</Chip>
                </div>
                <h2 id={titleId} ref={heading} tabIndex={-1} className="text-lg font-bold leading-tight outline-none md:text-xl">{event.name}</h2>
                <ul className="space-y-1.5 text-sm text-muted-foreground">
                  <li className="flex gap-2">
                    <CalendarDays size={16} className="mt-0.5 shrink-0" aria-hidden />
                    <span>
                      <span className="font-medium text-foreground">{when(event)}</span>
                      {event.doorsAt && <> · Doors {clock(event.doorsAt, event.timezone)}</>}
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <MapPin size={16} className="mt-0.5 shrink-0" aria-hidden />
                    <span><span className="font-medium text-foreground">{event.venue.name}</span>{event.venue.address && <span className="hidden md:inline"> · {event.venue.address}</span>}</span>
                  </li>
                  <li className="flex gap-2">
                    <Clock size={16} className="mt-0.5 shrink-0" aria-hidden />
                    <span>Park {clock(event.arriveFrom, event.timezone)} – {clock(event.departBy, event.timezone)}</span>
                  </li>
                </ul>
              </div>

              {event.state === 'canceled' && (
                <p role="status" className="rounded-xl bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300">
                  This event was canceled{detail?.canceledReason ? `: ${detail.canceledReason}` : '.'} Bookings are refunded in full.
                </p>
              )}

              <section aria-labelledby={`${titleId}-lots`}>
                <h3 id={`${titleId}-lots`} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Parking</h3>
                {detail ? (
                  <ul className="divide-y divide-border">
                    {detail.lots.map((l) => {
                      const s = lotStatus(l)
                      return (
                        <li key={l.id} className="flex items-center gap-3 py-2.5 md:py-3">
                          <span aria-hidden className={cn('grid size-9 shrink-0 place-items-center rounded-lg text-sm font-bold', TONE_CHIP[s.tone])}>P</span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium">{l.name}</div>
                            <div className="text-xs">
                              <span className={cn('font-medium', TONE_TEXT[s.tone])}>{s.label}</span>
                              <span className="text-muted-foreground"> · {l.walkMinutes} min walk</span>
                            </div>
                          </div>
                          {l.price && (
                            <div className={cn('text-base font-semibold tabular-nums', l.soldOut && 'text-muted-foreground line-through')}>
                              {money(l.price)}
                            </div>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                ) : failed ? (
                  <p className="py-3 text-sm text-muted-foreground">Parking for this event could not be loaded.</p>
                ) : (
                  <div className="space-y-3 py-3" aria-busy>
                    {[0, 1, 2].map((i) => <div key={i} className="h-9 animate-pulse rounded-lg bg-muted" />)}
                  </div>
                )}
              </section>
            </>
          ) : (
            <h2 id={titleId} className="sr-only">Loading event</h2>
          )}
        </div>
      </div>

      {event && bookable && (
        <div className="flex items-center gap-4 border-t border-border p-4">
          {event.priceFrom && (
            <div className="shrink-0">
              <div className="text-xs text-muted-foreground">From</div>
              <div className="text-xl font-bold leading-tight tabular-nums">{money(event.priceFrom)}</div>
            </div>
          )}
          <Button asChild size="lg" className="h-12 flex-1 rounded-xl text-base font-semibold">
            <a href={event.shopUrl} target="_blank" rel="noopener noreferrer">
              Book parking
              <ArrowUpRight aria-hidden />
              <span className="sr-only">(opens {event.organization.name}'s parking page)</span>
            </a>
          </Button>
        </div>
      )}
    </Sheet>
  )
}

/** The events in view, soonest first; or one venue's, opened from its pin. */
export function EventList({ events, atOneVenue, onSelect, onClose }: {
  events: EventSummary[]; atOneVenue: boolean; onSelect: (id: string) => void; onClose: () => void
}) {
  const titleId = useId()
  return (
    <Sheet labelledBy={titleId} onEscape={onClose}>
      <div className="flex items-center gap-3 border-b border-border py-2 pl-4 pr-2">
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="truncate text-lg font-bold">{atOneVenue ? events[0]?.venue.name : 'Upcoming events'}</h2>
          <p className="text-xs text-muted-foreground">
            {events.length} event{events.length === 1 ? '' : 's'} {atOneVenue ? 'here' : 'in this area'} · next {DAYS_AHEAD} days
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className={cn(ICON_BUTTON, 'hover:bg-muted')}>
          <X size={20} />
        </button>
      </div>
      {events.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted-foreground">No events in this area. Move the map to see more.</p>
      ) : (
        <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto overscroll-contain">
          {events.map((e) => {
            const tile = dateTile(e)
            const s = eventStatus(e)
            return (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => onSelect(e.id)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left outline-none transition-colors hover:bg-muted/60 focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <div className="grid w-12 shrink-0 place-items-center rounded-xl bg-violet-500/10 py-1.5 text-violet-700 dark:text-violet-300" aria-hidden>
                    <span className="text-[10px] font-bold tracking-wider">{tile.weekday}</span>
                    <span className="text-lg font-bold leading-none">{tile.day}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={cn('truncate font-semibold', e.state === 'canceled' && 'line-through')}>{e.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{when(e)} · {e.venue.name}</div>
                    <div className="mt-1 flex items-center gap-2 text-xs">
                      <Chip className={cn('h-5 px-2 text-[11px]', TONE_CHIP[s.tone])}>{s.label}</Chip>
                      {e.priceFrom && <span className="font-medium">from {money(e.priceFrom)}</span>}
                    </div>
                  </div>
                  <EventImage event={e} className="size-14 shrink-0 rounded-xl" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </Sheet>
  )
}

const CHIP = 'flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold shadow-md ring-1 ring-black/5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring dark:ring-white/10'

/** The Events toggle, and the list of those in view beside it. */
export function EventsControl({ on, count, onToggle, onList }: {
  on: boolean; count: number; onToggle: () => void; onList: () => void
}) {
  return (
    <div className="absolute left-3 top-3 z-10 flex gap-2">
      <button
        type="button"
        aria-pressed={on}
        onClick={onToggle}
        className={cn(CHIP, on ? 'bg-violet-600 text-white hover:bg-violet-700' : 'bg-background/95 text-foreground backdrop-blur hover:bg-muted')}
      >
        <Ticket size={16} aria-hidden />
        Events
      </button>
      {on && count > 0 && (
        <button type="button" onClick={onList} className={cn(CHIP, 'bg-background/95 text-foreground backdrop-blur hover:bg-muted')}>
          <List size={16} aria-hidden />
          {count} upcoming
        </button>
      )}
    </div>
  )
}
